import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useCallback,
} from "react";

const STORAGE_KEY = "app_user_v1";
const STORAGE_TOKEN_KEY = "app_token_v1";
const API_URL = "https://jrco.techxol.net/api/method/login";
const API_AUTH = "token eaf13fbdec090d4:cfea585ea399496";

const UserContext = createContext();

const initialState = {
  user: null,
  token: null,
  loading: false,
  error: null,
};

function reducer(state, action) {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: true, error: null };
    case "SET_ERROR":
      return { ...state, loading: false, error: action.payload };
    case "SET_AUTH":
      return {
        ...state,
        user: action.payload.user ?? null,
        token: action.payload.token ?? null,
        loading: false,
        error: null,
      };
    case "CLEAR_AUTH":
      return { user: null, token: null, loading: false, error: null };
    case "UPDATE_USER":
      return { ...state, user: { ...(state.user || {}), ...action.payload } };
    default:
      return state;
  }
}

export function UserProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const rawUser = localStorage.getItem(STORAGE_KEY);
      const rawToken = localStorage.getItem(STORAGE_TOKEN_KEY);
      const user = rawUser ? JSON.parse(rawUser) : null;
      const token = rawToken ?? null;
      if (user || token) {
        dispatch({ type: "SET_AUTH", payload: { user, token } });
      }
    } catch (e) {
      console.error("Failed to read user from localStorage", e);
    }
  }, []);

  // Persist user/token
  const persist = useCallback((user, token) => {
    try {
      if (user)
        localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
      else localStorage.removeItem(STORAGE_KEY);
      if (token)
        localStorage.setItem(STORAGE_TOKEN_KEY, token);
      else localStorage.removeItem(STORAGE_TOKEN_KEY);
    } catch (e) {
      console.error("Failed to persist auth", e);
    }
  }, []);

  // ---- LOGIN via API ----
  const login = useCallback(
    async (email, password) => {
      dispatch({ type: "SET_LOADING" });
      try {
        const response = await fetch(API_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: API_AUTH,
          },
          body: JSON.stringify({ usr: email, pwd: password }),
        });

        if (!response.ok) {
          const errText = await response.text();
          throw new Error(`Login failed: ${errText}`);
        }

        const data = await response.json();

        // Depending on your API structure, adjust this part:
        const user = data?.user || data?.message?.user || { email };
        const token = data?.token || data?.message?.token || API_AUTH;

        dispatch({ type: "SET_AUTH", payload: { user, token } });
        persist(user, token);
        return user;
      } catch (err) {
        console.error("Login error:", err);
        dispatch({ type: "SET_ERROR", payload: err.message });
        throw err;
      }
    },
    [persist]
  );

  // ---- LOGOUT ----
  const logout = useCallback(() => {
    dispatch({ type: "CLEAR_AUTH" });
    persist(null, null);
  }, [persist]);

  // ---- UPDATE USER ----
  const updateUser = useCallback(
    (patch) => {
      dispatch({ type: "UPDATE_USER", payload: patch });
      const nextUser = { ...(state.user || {}), ...patch };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(nextUser));
      } catch (e) {
        console.error("Failed to persist updated user", e);
      }
    },
    [state.user]
  );

  const value = {
    user: state.user,
    token: state.token,
    loading: state.loading,
    error: state.error,
    isAuthenticated: !!state.token,
    login,
    logout,
    updateUser,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUser must be used within a UserProvider");
  return ctx;
}

export default UserContext;
